// SuperAdminDashboard.js

import jwtDecode from 'jwt-decode';  // fixed import (no curly braces)
import React, { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import "./SuperAdminDashboard.css";
import ConfirmPopup from "./ConfirmPopup";
import { secureStorage } from "./utils/secureStorage";

// Toast Component
const Toast = ({ message, onClose }) => (
  <div className="toast">
    {message}
    <button onClick={onClose}>×</button>
  </div>
);

// Overlay Modal
const OverlayModal = ({ onClose, children }) => {
  const overlayRef = useRef(null);
  useEffect(() => {
    const handleClickOutside = e => {
      if (overlayRef.current && e.target === overlayRef.current) onClose();
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [onClose]);

  return <div className="modal-overlay" ref={overlayRef}>{children}</div>;
};

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://127.0.0.1:8000";

const SuperAdminDashboard = () => {
  const [tab, setTab] = useState("dashboard");
  const [organizations, setOrganizations] = useState([]);
  const [users, setUsers] = useState([]);
  const [tiers, setTiers] = useState([]);
  const [databases, setDatabases] = useState([]);
  const [faissDocs, setFaissDocs] = useState([]);
  const [analytics, setAnalytics] = useState({ total_questions: 0, total_users: 0, total_organizations: 0 });
  const [toastMsg, setToastMsg] = useState("");
  const [confirmAction, setConfirmAction] = useState(null);
  const [selectedOrgId, setSelectedOrgId] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [newUserData, setNewUserData] = useState({ email: "", password: "", role: "staff", organization_id: "" });
  const [newDbData, setNewDbData] = useState({ org_id: "", name: "", db_type: "", connection_string: "" });
  const [showUserModal, setShowUserModal] = useState(false);
  const [showDbModal, setShowDbModal] = useState(false);
  const [passwordInputs, setPasswordInputs] = useState({});

  const makeAuthorizedRequest = async (config) => {
    const token = secureStorage.getItem("token");
    try {
      return await axios({ 
        ...config, 
        baseURL: API_BASE_URL,
        headers: { 
          ...config.headers, 
          Authorization: `Bearer ${token}` 
        } 
      });
    } catch (err) {
      if (err.response?.status === 401) {
        try {
          const refreshRes = await axios.post(`${API_BASE_URL}/refresh`, { refresh_token: secureStorage.getItem("refreshToken") });
          secureStorage.setItem("token", refreshRes.data.access_token);
          return await axios({ 
            ...config, 
            baseURL: API_BASE_URL,
            headers: { 
              ...config.headers, 
              Authorization: `Bearer ${refreshRes.data.access_token}` 
            } 
          });
        } catch (refreshErr) {
          console.error("Refresh token failed:", refreshErr);
          secureStorage.removeItem("token");
          secureStorage.removeItem("refreshToken");
          window.location.reload();
          throw refreshErr;
        }
      }
      throw err;
    }
  };

  const showToast = msg => {
    setToastMsg(msg);
    setTimeout(() => setToastMsg(""), 3000);
  };

  const fetchAll = () => {
    makeAuthorizedRequest({ method: "GET", url: "/superadmin/organizations" })
      .then(r => setOrganizations(r.data))
      .catch(() => showToast("❌ Failed to load organizations"));

    makeAuthorizedRequest({ method: "GET", url: "/superadmin/users" })
      .then(r => setUsers(r.data))
      .catch(() => showToast("❌ Failed to load users"));

    makeAuthorizedRequest({ method: "GET", url: "/superadmin/pricing-tiers" })
      .then(r => setTiers(r.data))
      .catch(() => showToast("❌ Failed to load pricing tiers"));

    if (selectedOrgId) {
      makeAuthorizedRequest({ method: "GET", url: `/superadmin/faiss_docs/${selectedOrgId}` })
        .then(r => setFaissDocs(r.data.files))
        .catch(() => showToast("❌ Failed to load FAISS documents"));

      makeAuthorizedRequest({ method: "GET", url: `/superadmin/databases/${selectedOrgId}` })
        .then(r => setDatabases(r.data))
        .catch(() => showToast("❌ Failed to load databases"));
    }
  };

  useEffect(fetchAll, [selectedOrgId]);

  const fetchAnalytics = () => {
    if (!startDate || !endDate) return;
    makeAuthorizedRequest({ method: "GET", url: `/superadmin/analytics/global?start_date=${startDate}&end_date=${endDate}` })
      .then(r => setAnalytics(r.data))
      .catch(() => showToast("❌ Failed to load analytics"));
  };

  const [indexProgress, setIndexProgress] = useState(0);
  const [checkingProgress, setCheckingProgress] = useState(false);

  const pollIndexingProgress = (organizationId) => {
    let initialPollDone = false;

    const interval = setInterval(async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/superadmin/index-progress/${organizationId}`);
        const data = await res.json();

        if (data && typeof data.progress === "number") {
          if (!initialPollDone) {
            setCheckingProgress(true);
            initialPollDone = true;
          }

          setIndexProgress(data.progress);

          if (data.progress >= 100) {
            setTimeout(() => {
              clearInterval(interval);
              setCheckingProgress(false);
              showToast("✅ Indexing complete");
            }, 2000);
          }
        }
      } catch (error) {
        console.error("❌ Failed to fetch progress:", error);
        clearInterval(interval);
        setCheckingProgress(false);
      }
    }, 3000);
  };

  const handlers = {
    assignTier: (orgId, tierId) =>
      makeAuthorizedRequest({ method: "PUT", url: `/superadmin/organizations/${orgId}/assign-tier/${tierId}` })
        .then(fetchAll)
        .then(() => showToast("✔️ Tier assigned"))
        .catch(() => showToast("❌ Failed to assign tier")),

    toggleSuspendOrg: (orgId, shouldReactivate) => {
      const action = shouldReactivate ? "reactivate" : "suspend";
      const endpoint = shouldReactivate
        ? `/superadmin/organizations/${orgId}/reactivate`
        : `/superadmin/organizations/${orgId}/suspend`;

      const verbMap = {
        suspend: "suspended",
        reactivate: "reactivated",
      };

      setConfirmAction({
        message: `Are you sure you want to ${action} this organization?`,
        onConfirm: () =>
          makeAuthorizedRequest({ method: "PUT", url: endpoint })
            .then(() => {
              showToast(`✔️ Organization ${verbMap[action]}`);
              setTimeout(fetchAll, 1000);
            })
            .catch(() => showToast(`❌ Failed to ${action}`))
            .finally(() => setConfirmAction(null)),
      });
    },

    deleteOrg: (orgId) =>
      setConfirmAction({
        message: "Are you sure you want to delete this organization?",
        onConfirm: () =>
          makeAuthorizedRequest({ method: "DELETE", url: `/superadmin/organizations/${orgId}` })
            .then(fetchAll)
            .then(() => showToast("🗑️ Organization deleted"))
            .catch(() => showToast("❌ Failed to delete organization"))
            .finally(() => setConfirmAction(null)),
      }),

    deleteUser: (userId) =>
      setConfirmAction({
        message: "Are you sure you want to delete this user?",
        onConfirm: () =>
          makeAuthorizedRequest({ method: "DELETE", url: `/superadmin/users/${userId}` })
            .then(fetchAll)
            .then(() => showToast("🗑️ User deleted"))
            .catch(() => showToast("❌ Failed to delete user"))
            .finally(() => setConfirmAction(null)),
      }),

    uploadPDF: () => {
      if (!selectedOrgId || !selectedFile) return showToast("Please select organization and file");

      const fd = new FormData();
      fd.append("file", selectedFile);

      makeAuthorizedRequest({ method: "POST", url: `/superadmin/upload_pdf/${selectedOrgId}`, data: fd })
        .then(() => {
          showToast("✅ PDF uploaded");
          setIndexProgress(0);
          setTimeout(() => pollIndexingProgress(selectedOrgId), 1000);
        })
        .catch(() => showToast("❌ Failed to upload PDF"))
        .finally(() => setSelectedFile(null));
    },

    rebuildIndex: () => {
      if (!selectedOrgId) return showToast("Please select an organization");

      makeAuthorizedRequest({ method: "POST", url: `/superadmin/rebuild_index/${selectedOrgId}` })
        .then(() => {
          showToast("✅ Index rebuild started");
          setIndexProgress(0);
          setTimeout(() => pollIndexingProgress(selectedOrgId), 1000);
        })
        .catch(() => showToast("❌ Failed to rebuild index"));
    },

    deleteDoc: (filename) =>
      makeAuthorizedRequest({ method: "DELETE", url: `/superadmin/faiss_docs/${selectedOrgId}/${filename}` })
        .then(fetchAll)
        .then(() => showToast("🗑️ Document deleted"))
        .catch(() => showToast("❌ Failed to delete document")),

    openUserModal: () => {
      setNewUserData({ ...newUserData, organization_id: selectedOrgId });
      setShowUserModal(true);
    },

    createUser: () => {
      makeAuthorizedRequest({ method: "POST", url: "/superadmin/users", data: newUserData })
        .then(() => {
          fetchAll();
          setShowUserModal(false);
          showToast("✔️ User created");
        })
        .catch(() => showToast("❌ Failed to create user"));
    },

    updateUserRole: (userId, role) => {
      makeAuthorizedRequest({
        method: "PUT",
        url: `/superadmin/users/${userId}/role`,
        data: { role },
      })
        .then(() => {
          showToast("✔️ User role updated");
          setTimeout(fetchAll, 2000);
        })
        .catch(() => showToast("❌ Failed to update user role"));
    },

    changeUserPassword: (userId, newPassword) => {
      if (!newPassword || newPassword.length < 6) {
        showToast("❌ Password must be at least 6 characters");
        return;
      }

      makeAuthorizedRequest({
        method: "POST",
        url: `/superadmin/users/${userId}/password`,
        data: { new_password: newPassword },
      })
        .then(() => {
          showToast("🔑 Password updated");
          setTimeout(fetchAll, 2000);
        })
        .catch(() => showToast("❌ Failed to update password"));
    },

    openDbModal: () => {
      setNewDbData({ ...newDbData, org_id: selectedOrgId });
      setShowDbModal(true);
    },

    createDb: () => {
      makeAuthorizedRequest({ method: "POST", url: `/superadmin/databases/${newDbData.org_id}`, data: newDbData })
        .then(() => {
          fetchAll();
          setShowDbModal(false);
          showToast("✔️ Database added");
        })
        .catch(() => showToast("❌ Failed to add database"));
    },

    deleteDb: (dbId) =>
      makeAuthorizedRequest({ method: "DELETE", url: `/superadmin/databases/${dbId}` })
        .then(fetchAll)
        .then(() => showToast("🗑️ Database deleted"))
        .catch(() => showToast("❌ Failed to delete database")),
  };






// // SuperAdminDashboard.js

// import { jwtDecode } from 'jwt-decode';
// import React, { useEffect, useState, useCallback, useRef } from "react";
// import axios from "axios";
// import "./SuperAdminDashboard.css";
// import ConfirmPopup from "./ConfirmPopup";

// // Toast Component
// const Toast = ({ message, onClose }) => (
//   <div className="toast">
//     {message}
//     <button onClick={onClose}>×</button>
//   </div>
// );

// // Overlay Modal
// const OverlayModal = ({ onClose, children }) => {
//   const overlayRef = useRef(null);
//   useEffect(() => {
//     const handleClickOutside = e => {
//       if (overlayRef.current && e.target === overlayRef.current) onClose();
//     };
//     document.addEventListener("mousedown", handleClickOutside);
//     return () => document.removeEventListener("mousedown", handleClickOutside);
//   }, [onClose]);

//   return <div className="modal-overlay" ref={overlayRef}>{children}</div>;
// };

// const SuperAdminDashboard = () => {
//   const [tab, setTab] = useState("dashboard");
//   const [organizations, setOrganizations] = useState([]);
//   const [users, setUsers] = useState([]);
//   const [tiers, setTiers] = useState([]);
//   const [databases, setDatabases] = useState([]);
//   const [faissDocs, setFaissDocs] = useState([]);
//   const [analytics, setAnalytics] = useState({ total_questions:0, total_users:0, total_organizations:0 });
//   const [toastMsg, setToastMsg] = useState("");
//   const [confirmAction, setConfirmAction] = useState(null);
//   const [selectedOrgId, setSelectedOrgId] = useState("");
//   const [startDate, setStartDate] = useState("");
//   const [endDate, setEndDate] = useState("");
//   const [selectedFile, setSelectedFile] = useState(null);
//   const [newUserData, setNewUserData] = useState({ email:"", password:"", role:"staff", organization_id:"" });
//   const [newDbData, setNewDbData] = useState({ org_id:"", name:"", db_type:"", connection_string:"" });
//   const [showUserModal, setShowUserModal] = useState(false);
//   const [showDbModal, setShowDbModal] = useState(false);
//   const [passwordInputs, setPasswordInputs] = useState({});


//   const makeAuthorizedRequest = async (config) => {
//     const token = localStorage.getItem("token");
//     try {
//       return await axios({ ...config, headers: { ...config.headers, Authorization: `Bearer ${token}` } });
//     } catch (err) {
//       if (err.response?.status === 401) {
//         const refreshRes = await axios.post("/refresh", { refresh_token: localStorage.getItem("refreshToken") });
//         localStorage.setItem("token", refreshRes.data.access_token);
//         return await axios({ ...config, headers: { ...config.headers, Authorization: `Bearer ${refreshRes.data.access_token}` } });
//       }
//       throw err;
//     }
//   };

//   const showToast = msg => {
//     setToastMsg(msg);
//     setTimeout(() => setToastMsg(""), 3000);
//   };

//   const fetchAll = () => {
//     makeAuthorizedRequest({ method: "GET", url: "/superadmin/organizations" }).then(r => setOrganizations(r.data)).catch(() => showToast("❌ Orgs load"));
//     makeAuthorizedRequest({ method: "GET", url: "/superadmin/users" }).then(r => setUsers(r.data)).catch(() => showToast("❌ Users load"));
//     makeAuthorizedRequest({ method: "GET", url: "/superadmin/pricing-tiers" }).then(r => setTiers(r.data)).catch(() => showToast("❌ Tiers load"));
//     if (selectedOrgId) {
//       makeAuthorizedRequest({ method:"GET", url:`/superadmin/faiss_docs/${selectedOrgId}` }).then(r=>setFaissDocs(r.data.files)).catch(()=>showToast("❌ FAISS docs load"));
//       makeAuthorizedRequest({ method:"GET", url:`/superadmin/databases/${selectedOrgId}` }).then(r=>setDatabases(r.data)).catch(()=>showToast("❌ DB load"));
//     }
//   };

//   useEffect(fetchAll, [selectedOrgId]);

//   const fetchAnalytics = () => {
//     if (!startDate || !endDate) return;
//     makeAuthorizedRequest({method:"GET",url:`/superadmin/analytics/global?start_date=${startDate}&end_date=${endDate}`})
//       .then(r=>setAnalytics(r.data))
//       .catch(()=>showToast("❌ Analytics load"));
//   };

//   const [indexProgress, setIndexProgress] = useState(0);
//   const [checkingProgress, setCheckingProgress] = useState(false);

//   const pollIndexingProgress = (organizationId) => {
//     let initialPollDone = false;

//     const interval = setInterval(async () => {
//       try {
//         const res = await fetch(`/superadmin/index-progress/${organizationId}`);
//         const data = await res.json();

//         if (data && typeof data.progress === "number") {
//           if (!initialPollDone) {
//             setCheckingProgress(true);
//             initialPollDone = true;
//           }

//           setIndexProgress(data.progress);

//           if (data.progress >= 100) {
//             setTimeout(() => {
//               clearInterval(interval);
//               setCheckingProgress(false);
//               showToast("✅ Indexing complete");
//             }, 2000);
//           }
//         }
//       } catch (error) {
//         console.error("❌ Failed to fetch progress:", error);
//         clearInterval(interval);
//         setCheckingProgress(false);
//       }
//     }, 3000); // Poll every 3 seconds
//   };

//   const handlers = {
//     assignTier: (orgId, tierId) => makeAuthorizedRequest({method:"PUT",url:`/superadmin/organizations/${orgId}/assign-tier/${tierId}`})
//       .then(fetchAll).then(()=>showToast("✔️ Tier assigned")).catch(()=>showToast("❌ Tier assign")),

//     toggleSuspendOrg: (orgId, shouldReactivate) => {
//       const action = shouldReactivate ? "reactivate" : "suspend";
//       const endpoint = shouldReactivate
//         ? `/superadmin/organizations/${orgId}/reactivate`
//         : `/superadmin/organizations/${orgId}/suspend`;

//       const verbMap = {
//         suspend: "suspended",
//         reactivate: "reactivated"
//       };

//       setConfirmAction({
//         message: `Sure to ${action}?`,
//         onConfirm: () =>
//           makeAuthorizedRequest({ method: "PUT", url: endpoint })
//             .then(() => {
//               showToast(`✔️ Organization ${verbMap[action]}`);
//               setTimeout(fetchAll, 1000);
//             })
//             .catch(() => showToast(`❌ Failed to ${action}`))
//             .finally(() => setConfirmAction(null))
//       });
//     },



//     deleteOrg: orgId => setConfirmAction({
//       message:"Delete this organization?",
//       onConfirm: () => makeAuthorizedRequest({method:"DELETE",url:`/superadmin/organizations/${orgId}`})
//         .then(fetchAll).then(()=>showToast("🗑️ Deleted")).catch(()=>showToast("❌ Delete"))
//         .finally(()=>setConfirmAction(null))
//     }),

//     deleteUser: userId => setConfirmAction({
//       message:"Delete this user?",
//       onConfirm: () => makeAuthorizedRequest({method:"DELETE",url:`/superadmin/users/${userId}`})
//         .then(fetchAll).then(()=>showToast("🗑️ User deleted")).catch(()=>showToast("❌ Delete user"))
//         .finally(()=>setConfirmAction(null))
//     }),

//     uploadPDF: () => {
//       if (!selectedOrgId || !selectedFile) return showToast("Select org+file");
//       const fd = new FormData();
//       fd.append("file", selectedFile);
//       makeAuthorizedRequest({ method: "POST", url: `/superadmin/upload_pdf/${selectedOrgId}`, data: fd })
//         .then(() => {
//           showToast("✅ Uploaded");
//           setIndexProgress(0);
//           setTimeout(() => pollIndexingProgress(selectedOrgId), 1000);
//         })
//         .catch(() => showToast("❌ Upload"))
//         .finally(() => setSelectedFile(null));
//     },
    
//     rebuildIndex: () => {
//       if (!selectedOrgId) return showToast("Select org");
//       makeAuthorizedRequest({ method: "POST", url: `/superadmin/rebuild_index/${selectedOrgId}` })
//         .then(() => {
//           showToast("✅ Rebuilding");
//           setIndexProgress(0);
//           setTimeout(() => pollIndexingProgress(selectedOrgId), 1000);
//         })
//         .catch(() => showToast("❌ Rebuild"));
//     },


//     deleteDoc: filename => makeAuthorizedRequest({method:"DELETE",url:`/superadmin/faiss_docs/${selectedOrgId}/${filename}`})
//       .then(fetchAll).then(()=>showToast("🗑️ Doc deleted")).catch(()=>showToast("❌ Delete doc")),

//     openUserModal: () => { setNewUserData({...newUserData, organization_id:selectedOrgId}); setShowUserModal(true); },
//     createUser: () => {
//       makeAuthorizedRequest({method:"POST",url:"/superadmin/users",data:newUserData})
//         .then(()=>{fetchAll(); setShowUserModal(false); showToast("✔️ User created");})
//         .catch(()=>showToast("❌ Create user"));
//     },

//     updateUserRole: (userId, role) => {
//       makeAuthorizedRequest({
//         method: "PUT",
//         url: `/superadmin/users/${userId}/role`,
//         data: { role },
//       })
//         .then(() => {
//           showToast("✔️ Role updated");
//           setTimeout(() => {
//             fetchAll();
//           }, 2000); // 2 seconds is enough to let the toast show
//         })
//         .catch(() => showToast("❌ Failed to update role"));
//     },

//     changeUserPassword: (userId, newPassword) => {
//       if (!newPassword || newPassword.length < 6) {
//         showToast("❌ Password must be at least 6 characters");
//         return;
//       }
    
//       makeAuthorizedRequest({
//         method: "POST",
//         url: `/superadmin/users/${userId}/password`,
//         data: { new_password: newPassword },
//       })
//         .then(() => {
//           showToast("🔑 Password updated");
//           setTimeout(() => {
//             fetchAll(); // Delay to give toast time
//           }, 2000); // 2 seconds
//         })
//         .catch(() => showToast("❌ Failed to update password"));
//     },
    

    

//     openDbModal: () => { setNewDbData({...newDbData, org_id:selectedOrgId}); setShowDbModal(true); },
//     createDb: () => {
//       makeAuthorizedRequest({method:"POST",url:`/superadmin/databases/${newDbData.org_id}`,data:newDbData})
//         .then(()=>{fetchAll(); setShowDbModal(false); showToast("✔️ DB added");})
//         .catch(()=>showToast("❌ Add db"));
//     },
//     deleteDb: dbId => makeAuthorizedRequest({method:"DELETE",url:`/superadmin/databases/${dbId}`})
//       .then(fetchAll).then(()=>showToast("🗑️ DB deleted")).catch(()=>showToast("❌ Del DB"))
    
    
//   };

  return (
    <div className="dashboard-container">
      <h2 className="dashboard-title">🌍 Super Admin Dashboard</h2>
      <div className="tabs">
        {["dashboard","faissDocs","upload","rebuild","databases"].map(t =>
          <button key={t} onClick={()=>setTab(t)} className={tab===t?"active":""}>{t}</button>
        )}
      </div>

      {/* GLOBAL DASHBOARD */}
      {tab==="dashboard" && (
        <div className="section-wrapper">
          <div className="analytics-section">
            <h3>📊 Global Analytics</h3>
            <div className="date-range">
              <input type="date" value={startDate} onChange={e=>setStartDate(e.target.value)}/>
              <input type="date" value={endDate} onChange={e=>setEndDate(e.target.value)}/>
              <button onClick={fetchAnalytics}>Fetch</button>
            </div>
            <p>Total Questions: {analytics.total_questions}</p>
            <p>Total Users: {analytics.total_users}</p>
            <p>Total Organizations: {analytics.total_organizations}</p>
          </div>

          <div className="section-title">🏢 Organizations</div>
          {organizations.map(org => (
            <div key={org.id} className="org-card">
              <p><strong>{org.name}</strong> (ID: {org.id})</p>
              <button onClick={()=>handlers.toggleSuspendOrg(org.id, !org.is_active)}>
                {org.is_active?"Suspend":"Reactivate"}
              </button>
              <button onClick={()=>handlers.deleteOrg(org.id)}>Delete</button>
              <select value={org.tier_id || ""} onChange={e=>handlers.assignTier(org.id, e.target.value)}>
                <option value="">Assign Tier</option>
                {tiers.map(tier => <option key={tier.id} value={tier.id}>{tier.name}</option>)}
              </select>
            </div>
          ))}

          <div className="section-title">👥 Users</div>
          <button className="dashboard-button" onClick={handlers.openUserModal}>+ Add User</button>
          {users.map((user) => (
            <div key={user.id} className="user-card">
              <span>{user.email} — Org: {user.organization_id}</span>

              {/* Role Dropdown */}
              <select
                value={user.role}
                onChange={(e) => handlers.updateUserRole(user.id, e.target.value)}
              >
                <option value="staff">Staff</option>
                {/* <option value="executive">Executive</option> */}
                <option value="admin">Admin</option>
              </select>

              {/* Password Change Input */}
              <input
                type="password"
                placeholder="New Password"
                value={passwordInputs[user.id] || ""}
                onChange={(e) =>
                  setPasswordInputs({ ...passwordInputs, [user.id]: e.target.value })
                }
                style={{ marginLeft: "10px" }}
              />
              <button
                onClick={() => {
                  handlers.changeUserPassword(user.id, passwordInputs[user.id]);
                  setPasswordInputs({ ...passwordInputs, [user.id]: "" });
                }}
              >
                Change Password
              </button>

              <button onClick={() => handlers.deleteUser(user.id)} style={{ color: "red" }}>
                Delete
              </button>
            </div>
          ))}


          {/* <div className="section-title">👥 Users</div>
          <button className="dashboard-button" onClick={handlers.openUserModal}>+ Add User</button>
          {users.map(user => (
            <div key={user.id} className="user-card">
              <span>{user.email} — {user.role} — Org: {user.organization_id}</span>
              <button onClick={()=>handlers.deleteUser(user.id)}>Delete</button>
            </div>
          ))} */}
        </div>
      )}

      {/* FAISS DOCS */}
      {tab==="faissDocs" && (
        <div className="section-wrapper">
          <select value={selectedOrgId} onChange={e=>setSelectedOrgId(e.target.value)}>
            <option value="">Select Organization</option>
            {organizations.map(org => <option key={org.id} value={org.id}>{org.name}</option>)}
          </select>
          <div className="section-title">📦 FAISS Documents</div>
          {faissDocs.map(f => (
            <div key={f} className="doc-item">
              <span>{f}</span>
              <button onClick={()=>handlers.deleteDoc(f)}>Delete</button>
            </div>
          ))}
        </div>
      )}

      {/* UPLOAD PDFs */}
      {tab === "upload" && (
        <div className="section-wrapper">
          <select value={selectedOrgId} onChange={e => setSelectedOrgId(e.target.value)}>
            <option value="">Select Organization</option>
            {organizations.map(org => (
              <option key={org.id} value={org.id}>
                {org.name}
              </option>
            ))}
          </select>
          <div className="upload-controls">
            <input
              type="file"
              accept=".pdf,.docx,.pptx,.txt,.md"
              onChange={e => setSelectedFile(e.target.files[0])}
            />
            <button onClick={handlers.uploadPDF}>Upload File</button>
          </div>

          {/* Indexing progress bar */}
          {checkingProgress && (
            <div style={{ marginTop: "8px", width: "100%" }}>
              <p>Indexing Progress: {indexProgress}%</p>
              <div
                style={{
                  backgroundColor: "#ddd",
                  borderRadius: "4px",
                  height: "12px",
                  width: "100%",
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    width: `${indexProgress}%`,
                    backgroundColor: "#4caf50",
                    height: "100%",
                    transition: "width 0.3s ease-in-out",
                  }}
                />
              </div>
            </div>
          )}
        </div>
      )}

      {/* REBUILD INDEX */}
      {tab === "rebuild" && (
        <div className="section-wrapper">
          <select value={selectedOrgId} onChange={e => setSelectedOrgId(e.target.value)}>
            <option value="">Select Organization</option>
            {organizations.map(org => (
              <option key={org.id} value={org.id}>
                {org.name}
              </option>
            ))}
          </select>
          <button onClick={handlers.rebuildIndex}>🔁 Rebuild FAISS Index</button>

          {/* Indexing progress bar */}
          {checkingProgress && (
            <div style={{ marginTop: "8px", width: "100%" }}>
              <p>Indexing Progress: {indexProgress}%</p>
              <div
                style={{
                  backgroundColor: "#ddd",
                  borderRadius: "4px",
                  height: "12px",
                  width: "100%",
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    width: `${indexProgress}%`,
                    backgroundColor: "#4caf50",
                    height: "100%",
                    transition: "width 0.3s ease-in-out",
                  }}
                />
              </div>
            </div>
          )}
        </div>
      )}




      {/* DATABASES */}
      {tab==="databases" && (
        <div className="section-wrapper">
          <select value={selectedOrgId} onChange={e=>setSelectedOrgId(e.target.value)}>
            <option value="">Select Organization</option>
            {organizations.map(org => <option key={org.id} value={org.id}>{org.name}</option>)}
          </select>
          <div className="section-title">🗄️ Databases</div>
          <button className="dashboard-button" onClick={handlers.openDbModal}>+ Add Database</button>
          {databases.map(db => (
            <div key={db.id} className="doc-item">
              <span>{db.name} ({db.db_type})</span>
              <button onClick={()=>handlers.deleteDb(db.id)}>Delete</button>
            </div>
          ))}
        </div>
      )}

      {/* MODALS */}

      {showUserModal && (
      <OverlayModal onClose={() => setShowUserModal(false)}>
        <div className="modal large-modal">
          <h3>Add User</h3>

          <input
            placeholder="Email"
            value={newUserData.email}
            onChange={(e) =>
              setNewUserData({ ...newUserData, email: e.target.value })
            }
          />

          <input
            type="password"
            placeholder="Password"
            value={newUserData.password}
            onChange={(e) =>
              setNewUserData({ ...newUserData, password: e.target.value })
            }
          />

          <select
            value={newUserData.role}
            onChange={(e) =>
              setNewUserData({ ...newUserData, role: e.target.value })
            }
          >
            <option value="">Select Role</option>
            <option value="staff">Staff</option>
            <option value="admin">Admin</option>
            {/* <option value="executive">Executive</option> */}
          </select>

          {/* Existing organization selector */}
          <select
            value={newUserData.organization_id || ""}
            onChange={(e) =>
              setNewUserData({
                ...newUserData,
                organization_id: e.target.value ? Number(e.target.value) : null,
                organization_name: "", // Clear org name if selecting from dropdown
              })
            }
          >
            <option value="">Select Existing Organization</option>
            {organizations.map((org) => (
              <option key={org.id} value={org.id}>
                {org.name} (ID: {org.id})
              </option>
            ))}
          </select>

          {/* OR new organization input */}
          <input
            placeholder="Or enter new organization name"
            value={newUserData.organization_name || ""}
            onChange={(e) =>
              setNewUserData({
                ...newUserData,
                organization_name: e.target.value,
                organization_id: null, // Clear dropdown selection if typing
              })
            }
          />

          <button className="dashboard-button" onClick={handlers.createUser}>
            Create
          </button>
        </div>
      </OverlayModal>
    )}

      {/* {showUserModal && (
        <OverlayModal onClose={()=>setShowUserModal(false)}>
          <div className="modal large-modal">
            <h3>Add User</h3>
            <input placeholder="Email" value={newUserData.email} onChange={e=>setNewUserData({...newUserData,email:e.target.value})}/>
            <input type="password" placeholder="Password" value={newUserData.password} onChange={e=>setNewUserData({...newUserData,password:e.target.value})}/>
            <select value={newUserData.role} onChange={e=>setNewUserData({...newUserData,role:e.target.value})}>
              <option value="staff">Staff</option><option value="admin">Admin</option>
            </select>
            <button className="dashboard-button" onClick={handlers.createUser}>Create</button>
          </div>
        </OverlayModal>
      )} */}

      {showDbModal && (
        <OverlayModal onClose={() => setShowDbModal(false)}>
          <div className="modal large-modal">
            <h3>Add Database</h3>
            <div className="db-form">
              <input
                name="name"
                placeholder="Name"
                value={newDbData.name || ""}
                onChange={(e) => setNewDbData({ ...newDbData, name: e.target.value })}
              />
              <input
                name="db_type"
                placeholder="DB Type"
                value={newDbData.db_type || ""}
                onChange={(e) => setNewDbData({ ...newDbData, db_type: e.target.value })}
              />
              <input
                name="host"
                placeholder="Host"
                value={newDbData.host || ""}
                onChange={(e) => setNewDbData({ ...newDbData, host: e.target.value })}
              />
              <input
                name="port"
                placeholder="Port"
                value={newDbData.port || ""}
                onChange={(e) => setNewDbData({ ...newDbData, port: e.target.value })}
              />
              <input
                name="username"
                placeholder="Username"
                value={newDbData.username || ""}
                onChange={(e) => setNewDbData({ ...newDbData, username: e.target.value })}
              />
              <input
                name="password"
                type="password"
                placeholder="Password"
                value={newDbData.password || ""}
                onChange={(e) => setNewDbData({ ...newDbData, password: e.target.value })}
              />
              <input
                name="database_name"
                placeholder="Database Name"
                value={newDbData.database_name || ""}
                onChange={(e) => setNewDbData({ ...newDbData, database_name: e.target.value })}
              />
            </div>
            <div className="modal-actions">
              <button className="dashboard-button" onClick={handlers.createDb}>
                Create Database
              </button>
              <button onClick={() => setShowDbModal(false)}>Close</button>
            </div>
          </div>
        </OverlayModal>
      )}


      {/* {showDbModal && (
        <OverlayModal onClose={()=>setShowDbModal(false)}>
          <div className="modal large-modal">
            <h3>Add Database</h3>
            <input placeholder="Name" value={newDbData.name} onChange={e=>setNewDbData({...newDbData,name:e.target.value})}/>
            <input placeholder="DB Type" value={newDbData.db_type} onChange={e=>setNewDbData({...newDbData,db_type:e.target.value})}/>
            <input placeholder="Connection String" value={newDbData.connection_string} onChange={e=>setNewDbData({...newDbData,connection_string:e.target.value})}/>
            <button className="dashboard-button" onClick={handlers.createDb}>Create</button>
          </div>
        </OverlayModal>
      )} */}

      {confirmAction && (
        <ConfirmPopup message={confirmAction.message} onConfirm={confirmAction.onConfirm} onCancel={()=>setConfirmAction(null)} />
      )}

      {toastMsg && <Toast message={toastMsg} onClose={()=>setToastMsg("")} />}
    </div>
  );
};

export default SuperAdminDashboard;

